// SpaceServicePage.tsx — Co Marca Admin — estilo CategoryPage
import Loading from '../../../components/Loading';
import React, { useEffect, useState } from 'react';
import { Edit, Trash2, Plus, History, Store, ChevronRight, Home, Package, Layers } from 'lucide-react';
import { fetchEspacioServicios, EspacioServicio } from '../../../services/espacioServiciosService';
import { fetchEspacios, fetchServicios, CatalogoItem } from '../../../services/catalogosService';
import AddSpaceServiceModal from '../../../components/SpaceService/AddSpaceServiceModal';
import EditSpaceServiceModal from '../../../components/SpaceService/EditSpaceServiceModal';
import DeleteSpaceServiceModal from '../../../components/SpaceService/DeleteSpaceServiceModal';
import Pagination from '../../../components/Pagination/Pagination';
import ExpiredSessionModal from '../../../components/Expired/ExpiredSessionModal';
import { useSessionExpiration } from '../../../hooks/useSessionExpiration';
import { useAuth } from '../../../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { useModelPermission } from '../../../hooks/useModelPermission';

const colors = {
    primary:        '#c1348b',
    primaryDark:    '#8f1f64',
    secondary:      '#61257b',
    secondaryLight: '#8b3fa8',
    accent:         '#ffd97c',
    accentDark:     '#ea8406',
};

const css = `
@import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@300;400;500;600;700;800;900&display=swap');
.ssp-page { font-family:'DM Sans',sans-serif; }
@keyframes sspFadeUp { from{opacity:0;transform:translateY(18px);}to{opacity:1;transform:translateY(0);} }
.ssp-fadeup   { animation:sspFadeUp .5s cubic-bezier(.16,1,.3,1) both; }
.ssp-fadeup-1 { animation:sspFadeUp .5s .08s cubic-bezier(.16,1,.3,1) both; }
.ssp-btn-primary {
    display:inline-flex;align-items:center;gap:7px;
    background:linear-gradient(135deg,${colors.primary},${colors.primaryDark});
    color:#fff;border:none;border-radius:11px;
    padding:9px 20px;font-size:.83rem;font-weight:800;
    cursor:pointer;font-family:'DM Sans',sans-serif;
    box-shadow:0 4px 14px rgba(193, 52, 139,.25);
    transition:all .22s cubic-bezier(.16,1,.3,1);white-space:nowrap;
}
.ssp-btn-primary:hover{transform:translateY(-2px);box-shadow:0 8px 22px rgba(193, 52, 139,.32);}
.ssp-btn-secondary {
    display:inline-flex;align-items:center;gap:7px;
    background:#fff;border:1.5px solid #e2e8f0;color:#374151;
    border-radius:11px;padding:9px 14px;font-size:.83rem;font-weight:700;
    cursor:pointer;font-family:'DM Sans',sans-serif;transition:all .2s;
}
.ssp-btn-secondary:hover{background:#f8fafc;border-color:${colors.secondary}40;color:${colors.secondary};}
.ssp-table-row{transition:background .15s;}
.ssp-table-row:hover{background:#fafcff;}
.ssp-action-btn{width:30px;height:30px;border-radius:9px;border:none;display:inline-flex;align-items:center;justify-content:center;cursor:pointer;transition:all .2s;}
.custom-scroll::-webkit-scrollbar{height:6px;width:6px;}
.custom-scroll::-webkit-scrollbar-track{background:transparent;}
.custom-scroll::-webkit-scrollbar-thumb{background-color:rgba(97, 37, 123,0.3);border-radius:20px;}
.custom-scroll::-webkit-scrollbar-thumb:hover{background-color:rgba(97, 37, 123,0.5);}
`;

const SpaceServicePage: React.FC = () => {
    const [servicios, setServicios]         = useState<EspacioServicio[]>([]);
    const [espaciosList, setEspaciosList]   = useState<CatalogoItem[]>([]);
    const [serviciosListCat, setServiciosListCat] = useState<CatalogoItem[]>([]);
    const [showAddModal, setShowAddModal]   = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [selectedServicio, setSelectedServicio] = useState<EspacioServicio|null>(null);
    const [currentPage, setCurrentPage]     = useState(1);
    const itemsPerPage = 10;
    const navigate = useNavigate();
    const { isSessionExpired, setIsSessionExpired } = useSessionExpiration();
    const { logout, user } = useAuth();
    const userLevelId = user?.user_level_id ?? 0;
    const perm = useModelPermission('espacioservicio', userLevelId);

    const loadServicios = () => {
        fetchEspacioServicios().then(data=>setServicios(data.sort((a,b)=>a.id-b.id))).catch(()=>{});
    };
    const loadCatalogos = () => {
        fetchEspacios().then(setEspaciosList).catch(()=>setEspaciosList([]));
        fetchServicios().then(setServiciosListCat).catch(()=>setServiciosListCat([]));
    };

    useEffect(() => {
        if (perm?.can_read) { loadServicios(); loadCatalogos(); }
        else { setServicios([]); setEspaciosList([]); setServiciosListCat([]); }
    }, [perm?.can_read]);

    const totalPages        = Math.ceil(servicios.length / itemsPerPage);
    const displayedServicios = servicios.slice((currentPage-1)*itemsPerPage, currentPage*itemsPerPage);
    const getNombre = (list: CatalogoItem[], id: number) => list.find(i=>i.id===id)?.nombre || id.toString();

    if (perm === null) return <Loading text="Cargando servicios de espacios..." />;

    return (
        <>
            <style>{css}</style>
            <div className="ssp-page" style={{ background:'#fff', minHeight:'100vh', padding:'clamp(24px,3vw,36px) clamp(20px,4vw,40px)' }}>

                {/* HEADER */}
                <div className="ssp-fadeup" style={{ marginBottom:'28px' }}>
                    <nav style={{ display:'flex', alignItems:'center', gap:'6px', fontSize:'.72rem', fontWeight:'600', color:'#94a3b8', marginBottom:'12px' }}>
                        <a href="/dashboard" style={{ color:'#94a3b8', textDecoration:'none', display:'flex', alignItems:'center', gap:'3px', transition:'color .2s' }}
                            onMouseEnter={e=>e.currentTarget.style.color=colors.primary}
                            onMouseLeave={e=>e.currentTarget.style.color='#94a3b8'}>
                            <Home size={11}/> Dashboard
                        </a>
                        <ChevronRight size={10} color="#e2e8f0"/>
                        <span style={{ color:colors.primary, fontWeight:'700' }}>Espacios de Servicios</span>
                    </nav>
                    <div style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between', flexWrap:'wrap', gap:'16px' }}>
                        <div>
                            <h1 style={{ fontSize:'clamp(1.5rem,3vw,2rem)', fontWeight:'900', color:'#0f172a', letterSpacing:'-.025em', lineHeight:1.1, marginBottom:'6px' }}>
                                Espacios de <span style={{ color:colors.primary }}>Servicios</span>
                            </h1>
                            <p style={{ fontSize:'.85rem', color:'#64748b', fontWeight:'500' }}>Gestiona las asociaciones entre espacios físicos y servicios</p>
                            <div style={{ display:'flex', gap:'5px', marginTop:'10px' }}>
                                {[colors.primary,colors.secondary,colors.accentDark,colors.secondaryLight].map((c,i)=>(
                                    <div key={i} style={{ height:'3px', width:i===0?'24px':'8px', borderRadius:'3px', background:c }}/>
                                ))}
                            </div>
                        </div>
                        <div style={{ display:'flex', gap:'10px', alignItems:'center', flexWrap:'wrap' }}>
                            <button className="ssp-btn-secondary" onClick={()=>navigate('/spaces-services/history')} title="Historial">
                                <History size={15} color={colors.secondary}/>
                            </button>
                            {perm.can_create && (
                                <button className="ssp-btn-primary" onClick={()=>setShowAddModal(true)}>
                                    <Plus size={15}/> Agregar
                                </button>
                            )}
                        </div>
                    </div>
                </div>

                {/* TABLA */}
                <div className="ssp-fadeup-1" style={{ background:'#fff', borderRadius:'20px', border:'1.5px solid #f1f5f9', boxShadow:'0 4px 24px rgba(0,0,0,.04)', overflow:'hidden' }}>

                    <ExpiredSessionModal isOpen={isSessionExpired} onClose={async()=>{ setIsSessionExpired(false); await logout(); }}/>
                    <AddSpaceServiceModal isOpen={showAddModal} onClose={()=>setShowAddModal(false)} onSuccess={()=>{ loadServicios(); loadCatalogos(); setShowAddModal(false); }}/>
                    <EditSpaceServiceModal isOpen={showEditModal} onClose={()=>{ setShowEditModal(false); setSelectedServicio(null); }} onSuccess={()=>{ loadServicios(); setShowEditModal(false); }} servicio={selectedServicio}/>
                    <DeleteSpaceServiceModal isOpen={showDeleteModal} onClose={()=>{ setShowDeleteModal(false); setSelectedServicio(null); }} onSuccess={()=>{ loadServicios(); setShowDeleteModal(false); }} servicio={selectedServicio}/>

                    {/* Contador */}
                    <div style={{ padding:'16px 24px', borderBottom:'1.5px solid #f1f5f9', display:'flex', alignItems:'center', justifyContent:'space-between' }}>
                        <div style={{ display:'flex', alignItems:'center', gap:'8px' }}>
                            <div style={{ width:'32px', height:'32px', borderRadius:'10px', background:`${colors.primary}12`, display:'flex', alignItems:'center', justifyContent:'center' }}>
                                <Layers size={15} color={colors.primary}/>
                            </div>
                            <span style={{ fontSize:'.82rem', fontWeight:'800', color:'#0f172a' }}>{servicios.length}</span>
                            <span style={{ fontSize:'.78rem', color:'#94a3b8', fontWeight:'500' }}>asociaciones registradas</span>
                        </div>
                        <span style={{ fontSize:'.72rem', color:'#94a3b8', fontWeight:'600' }}>Página {currentPage} de {totalPages||1}</span>
                    </div>

                    {perm.can_read && (
                        <>
                            <div className="custom-scroll" style={{ overflowX:'auto' }}>
                                <table style={{ width:'100%', borderCollapse:'collapse', fontFamily:"'DM Sans',sans-serif" }}>
                                    <thead>
                                        <tr style={{ background:'#fafcff', borderBottom:'1.5px solid #f1f5f9' }}>
                                            {['Servicio','Espacio','Fecha creación','Estado','Acciones'].map((h,i)=>(
                                                <th key={i} style={{ padding:'12px 18px', textAlign:'left', fontSize:'.68rem', fontWeight:'800', color:'#94a3b8', textTransform:'uppercase', letterSpacing:'.08em', whiteSpace:'nowrap' }}>
                                                    {h}
                                                </th>
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {displayedServicios.map(item=>(
                                            <tr key={item.id} className="ssp-table-row" style={{ borderBottom:'1px solid #f8fafc' }}>
                                                <td style={{ padding:'12px 18px', whiteSpace:'nowrap' }}>
                                                    <span style={{ display:'inline-flex', alignItems:'center', gap:'5px', background:`${colors.primary}10`, border:`1px solid ${colors.primary}22`, color:colors.primaryDark, borderRadius:'6px', padding:'2px 8px', fontSize:'.72rem', fontWeight:'700' }}>
                                                        <Package size={11}/> {getNombre(serviciosListCat, item.servicio)}
                                                    </span>
                                                </td>
                                                <td style={{ padding:'12px 18px', whiteSpace:'nowrap' }}>
                                                    <span style={{ display:'inline-flex', alignItems:'center', gap:'5px', background:`${colors.secondary}10`, border:`1px solid ${colors.secondary}22`, color:colors.secondary, borderRadius:'6px', padding:'2px 8px', fontSize:'.72rem', fontWeight:'700' }}>
                                                        <Store size={11}/> {getNombre(espaciosList, item.espacio)}
                                                    </span>
                                                </td>
                                                <td style={{ padding:'12px 18px', whiteSpace:'nowrap' }}>
                                                    <span style={{ fontSize:'.78rem', color:'#64748b', fontWeight:'500' }}>
                                                        {new Date(item.fecha_creacion).toLocaleDateString('es-ES',{day:'2-digit',month:'2-digit',year:'numeric'})}
                                                    </span>
                                                </td>
                                                <td style={{ padding:'12px 18px', whiteSpace:'nowrap' }}>
                                                    {item.estado ? (
                                                        <span style={{ display:'inline-flex', alignItems:'center', gap:'5px', background:`${colors.secondaryLight}14`, border:`1px solid ${colors.secondaryLight}30`, color:'#065f46', borderRadius:'100px', padding:'3px 10px', fontSize:'.7rem', fontWeight:'700' }}>
                                                            <span style={{ width:'5px', height:'5px', borderRadius:'50%', background:colors.secondaryLight, display:'inline-block' }}/>Activo
                                                        </span>
                                                    ) : (
                                                        <span style={{ display:'inline-flex', alignItems:'center', gap:'5px', background:`${colors.primary}10`, border:`1px solid ${colors.primary}22`, color:colors.primaryDark, borderRadius:'100px', padding:'3px 10px', fontSize:'.7rem', fontWeight:'700' }}>
                                                            <span style={{ width:'5px', height:'5px', borderRadius:'50%', background:colors.primary, display:'inline-block' }}/>Inactivo
                                                        </span>
                                                    )}
                                                </td>
                                                <td style={{ padding:'12px 18px', whiteSpace:'nowrap' }}>
                                                    <div style={{ display:'flex', gap:'6px' }}>
                                                        {perm.can_update && (
                                                            <button className="ssp-action-btn" onClick={()=>{ setSelectedServicio(item); setShowEditModal(true); }} title="Editar"
                                                                style={{ background:`${colors.secondary}12`, color:colors.secondary }}
                                                                onMouseEnter={e=>{ (e.currentTarget as HTMLButtonElement).style.background=colors.secondary; (e.currentTarget as HTMLButtonElement).style.color='#fff'; }}
                                                                onMouseLeave={e=>{ (e.currentTarget as HTMLButtonElement).style.background=`${colors.secondary}12`; (e.currentTarget as HTMLButtonElement).style.color=colors.secondary; }}>
                                                                <Edit size={14}/>
                                                            </button>
                                                        )}
                                                        {perm.can_delete && (
                                                            <button className="ssp-action-btn" onClick={()=>{ setSelectedServicio(item); setShowDeleteModal(true); }} title="Eliminar"
                                                                style={{ background:`${colors.primary}10`, color:colors.primary }}
                                                                onMouseEnter={e=>{ (e.currentTarget as HTMLButtonElement).style.background=colors.primary; (e.currentTarget as HTMLButtonElement).style.color='#fff'; }}
                                                                onMouseLeave={e=>{ (e.currentTarget as HTMLButtonElement).style.background=`${colors.primary}10`; (e.currentTarget as HTMLButtonElement).style.color=colors.primary; }}>
                                                                <Trash2 size={14}/>
                                                            </button>
                                                        )}
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                        {servicios.length===0 && (
                                            <tr>
                                                <td colSpan={5} style={{ padding:'60px 24px', textAlign:'center' }}>
                                                    <div style={{ display:'flex', flexDirection:'column', alignItems:'center', gap:'12px' }}>
                                                        <div style={{ width:'56px', height:'56px', borderRadius:'16px', background:`${colors.primary}10`, display:'flex', alignItems:'center', justifyContent:'center' }}>
                                                            <Layers size={24} color={colors.primary} style={{ opacity:.5 }}/>
                                                        </div>
                                                        <p style={{ fontSize:'1rem', fontWeight:'800', color:'#0f172a' }}>No hay espacios de servicios disponibles</p>
                                                        {perm.can_create && <p style={{ fontSize:'.82rem', color:'#94a3b8' }}>Agrega una nueva asociación para comenzar</p>}
                                                        <div style={{ display:'flex', gap:'5px', marginTop:'4px' }}>
                                                            {[colors.primary,colors.secondary,colors.accentDark,colors.secondaryLight].map((c,i)=>(
                                                                <div key={i} style={{ height:'2px', width:i===0?'16px':'6px', borderRadius:'2px', background:c, opacity:.4 }}/>
                                                            ))}
                                                        </div>
                                                    </div>
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                            {servicios.length>0 && (
                                <div style={{ borderTop:'1.5px solid #f1f5f9' }}>
                                    <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} itemsPerPage={itemsPerPage} totalItems={servicios.length}/>
                                </div>
                            )}
                        </>
                    )}
                </div>
            </div>
        </>
    );
};

export default SpaceServicePage;